import api from './api';

export interface RecipeMappingPayload {
  inventoryItemId: number;
  /** null/undefined = base recipe row applying to all portions without their own rows */
  portionId?: number | null;
  /** per-portion amount; ignored by the backend when batch fields are provided */
  quantityRequired?: number;
  /** batch mode: ingredient amount per batch (e.g. 3 KG rice) */
  batchQuantity?: number | null;
  /** batch mode: portions yielded per batch (e.g. 80 idlis) */
  batchYield?: number | null;
}

export interface EstimateLinePayload {
  menuItemId: number;
  portionId?: number | null;
  quantity: number;
}

export interface ManualSaleLinePayload {
  menuItemId: number;
  portionId?: number | null;
  quantity: number;
}

export interface ManualSalePayload {
  saleDate: string; // yyyy-MM-dd
  notes?: string;
  lines: ManualSaleLinePayload[];
}

export interface PlannerPlanPayload {
  name: string;
  lines: EstimateLinePayload[];
}

export interface PurchaseItemPayload {
  inventoryItemId: number;
  quantity: number;
}

export interface PurchasePayload {
  items: PurchaseItemPayload[];
  supplierName?: string;
  notes?: string;
  createExpense?: boolean;
  /** owner-entered amount paid; required when createExpense */
  expenseAmount?: number;
  paymentMode?: string;
}

export interface PrepIngredientPayload {
  ingredientItemId: number;
  quantityPerUnit?: number | null;
  batchQuantity?: number | null;
  batchYield?: number | null;
}

// --- Weekly paper sheets (printable stock/sales worksheets + photo import) ---

export type SheetType = 'STOCK' | 'SALES';
export type SheetStatus = 'DRAFT' | 'CONFIRMED' | 'CANCELLED';
export type WeekDay = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export interface SheetRowDays {
  MON: number | null;
  TUE: number | null;
  WED: number | null;
  THU: number | null;
  FRI: number | null;
  SAT: number | null;
  SUN: number | null;
}

export interface SheetRow {
  itemId?: number | null;
  menuItemId?: number | null;
  portionId?: number | null;
  itemName: string;
  portionName?: string | null;
  unit?: string | null;
  days: SheetRowDays;
  /** STOCK sheets only: physical count at week end */
  weekEndCount?: number | null;
  /** 0..1 extraction confidence; < 0.7 flags the row for review */
  confidence: number;
  warnings: string[];
}

/** A row the extractor could not match to a known item/dish — informational only */
export interface SheetUnmatchedRow {
  label: string;
  days: SheetRowDays;
}

export interface SheetImportResponse {
  id: number;
  sheetType: SheetType;
  category: string | null;
  weekStart: string; // yyyy-MM-dd (Monday)
  status: SheetStatus;
  templateVersion?: string;
  /** history-list responses may omit heavy rows */
  rows?: SheetRow[];
  unmatchedRows?: SheetUnmatchedRow[];
  /** import-time notices (e.g. sheet identified from header text, not QR) */
  warnings?: string[];
}

export interface SheetImportRequest {
  imageBase64: string;
  imageMimeType: string;
  imageKey?: string;
  typeHint?: SheetType;
  categoryHint?: string;
  weekStartHint?: string; // yyyy-MM-dd (Monday)
}

export interface SheetConfirmResponse {
  id: number;
  status: SheetStatus;
  receiptsCreated?: number;
  countAdjustments?: number;
  salesDaysUpserted?: number;
  warnings?: string[];
}

export const sheetsApi = {
  // Inventory categories for stock-sheet grouping (may include "Uncategorized")
  getCategories: () => api.get<string[]>('/api/vendor/inventory/sheets/categories'),

  // Printable PDF template — weekStart must be a Monday; omit category for SALES
  getTemplate: (params: { type: SheetType; category?: string; weekStart: string }) =>
    api.get('/api/vendor/inventory/sheets/template', { params, responseType: 'blob' }),

  // Photo of a filled sheet → extracted DRAFT for review
  importSheet: (data: SheetImportRequest) =>
    api.post<SheetImportResponse>('/api/vendor/inventory/sheets/import', data),

  // Save the reviewed draft (creates receipts / count adjustments / sales days)
  confirmSheet: (id: number, rows: SheetRow[]) =>
    api.post<SheetConfirmResponse>(`/api/vendor/inventory/sheets/${id}/confirm`, { rows }),

  cancelSheet: (id: number) => api.post(`/api/vendor/inventory/sheets/${id}/cancel`),

  getHistory: (limit = 20) =>
    api.get<SheetImportResponse[]>('/api/vendor/inventory/sheets', { params: { limit } }),

  getSheet: (id: number) => api.get<SheetImportResponse>(`/api/vendor/inventory/sheets/${id}`),
};

export const inventoryApi = {
  // Inventory items
  getItems: () => api.get('/api/vendor/inventory'),
  createItem: (data: { name: string; unit: string; currentStock: number; lowStockThreshold: number; category?: string | null }) =>
    api.post('/api/vendor/inventory', data),
  updateItem: (id: number, data: { name: string; unit: string; currentStock: number; lowStockThreshold: number; category?: string | null }) =>
    api.put(`/api/vendor/inventory/${id}`, data),
  deactivateItem: (id: number) => api.delete(`/api/vendor/inventory/${id}`),
  getLowStock: () => api.get('/api/vendor/inventory/low-stock'),
  getReport: () => api.get('/api/vendor/inventory/report'),
  adjustStock: (id: number, data: { quantity: number; type: string; notes?: string; occurredOn?: string }) =>
    api.post(`/api/vendor/inventory/${id}/adjust`, data),

  // Ingredient planner: estimate requirements for planned production
  estimate: (data: { lines: EstimateLinePayload[] }) =>
    api.post('/api/vendor/inventory/estimate', data),

  // Planner: sales-driven auto-fill ("plan like last Saturday")
  getSalesHistory: (from: string, to: string) =>
    api.get('/api/vendor/inventory/sales-history', { params: { from, to } }),

  // Planner: saved plans
  getPlans: () => api.get('/api/vendor/inventory/plans'),
  savePlan: (data: PlannerPlanPayload) => api.post('/api/vendor/inventory/plans', data),
  deletePlan: (id: number) => api.delete(`/api/vendor/inventory/plans/${id}`),

  // Planner: mark shopping list purchased (stock-in + optional expense)
  recordPurchase: (data: PurchasePayload) => api.post('/api/vendor/inventory/purchase', data),

  // Manual daily sales (standalone mode)
  getManualSales: (from?: string, to?: string) =>
    api.get('/api/vendor/manual-sales', { params: { from, to } }),
  createManualSale: (data: ManualSalePayload) => api.post('/api/vendor/manual-sales', data),
  updateManualSale: (id: number, data: ManualSalePayload) => api.put(`/api/vendor/manual-sales/${id}`, data),
  deleteManualSale: (id: number) => api.delete(`/api/vendor/manual-sales/${id}`),
  // Record actual usage for a short day (net-zero stock backfill + consume)
  recordManualSaleUsage: (id: number, data: { inventoryItemId: number; quantity: number }) =>
    api.post(`/api/vendor/manual-sales/${id}/record-usage`, data),

  // Prep recipes (kitchen-made inventory items)
  getPrepRecipe: (itemId: number) => api.get(`/api/vendor/inventory/${itemId}/prep-recipe`),
  setPrepRecipe: (itemId: number, data: { ingredients: PrepIngredientPayload[] }) =>
    api.put(`/api/vendor/inventory/${itemId}/prep-recipe`, data),
  producePrep: (itemId: number, data: { quantity: number; notes?: string }) =>
    api.post(`/api/vendor/inventory/${itemId}/produce`, data),

  // Reports
  getConsumptionVariance: (from: string, to: string) =>
    api.get('/api/vendor/inventory/reports/consumption-variance', { params: { from, to } }),

  // Overview dashboard: per-ingredient daily used / stock-in / wasted
  getDashboard: (from: string, to: string) =>
    api.get('/api/vendor/inventory/dashboard', { params: { from, to } }),

  // Recipe mappings
  getRecipe: (menuItemId: number) => api.get(`/api/vendor/recipes/menu-item/${menuItemId}`),
  suggestRecipe: (menuItemId: number, region?: string) =>
    api.post('/api/vendor/recipes/suggest', { menuItemId, region }),
  setRecipe: (menuItemId: number, data: { mappings: RecipeMappingPayload[] }) =>
    api.put(`/api/vendor/recipes/menu-item/${menuItemId}`, data),
  deleteMapping: (mappingId: number) => api.delete(`/api/vendor/recipes/${mappingId}`),
  getFoodCost: (menuItemId: number) => api.get(`/api/vendor/recipes/menu-item/${menuItemId}/food-cost`),

  // Audit logs — server-side filtered + paginated (Spring Page response:
  // { content, totalElements, ... }); from/to = business date yyyy-MM-dd
  getAuditLogs: (params?: {
    itemId?: number; changeType?: string; from?: string; to?: string;
    page?: number; size?: number;
  }) => api.get('/api/vendor/stock-audit', { params }),
  getItemAuditLogs: (itemId: number) => api.get(`/api/vendor/stock-audit/item/${itemId}`),
  getOrderAuditLogs: (orderId: number) => api.get(`/api/vendor/stock-audit/order/${orderId}`),

  // Stock count sessions — physical-count worksheet + variance capture
  createStockCount: () => api.post('/api/vendor/inventory/stock-count'),
  getStockCount: (id: number) => api.get(`/api/vendor/inventory/stock-count/${id}`),
  getStockCountHistory: () => api.get('/api/vendor/inventory/stock-count/history'),
};
